import {
    ActionIcon,
    Badge,
    Button,
    Divider,
    Group,
    Input,
    Kbd,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useField } from "@mantine/form";
import { useClipboard, useHotkeys } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconClockX, IconCopy, IconRosetteDiscountCheck, IconSearch, IconTrash, IconUserCog } from "@tabler/icons-react";
import { queryOptions, useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, useRef, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { PropertyBadge } from "shared/components/global/PropertyBadge";
import { getRelativeTime } from "shared/components/RelativeTime";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useAside } from "shared/utils/aside";
import { catching, titlecase } from "shared/utils/fns";
import { maybe, some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { graphql } from "src/gql";
import { Cm, Language, UserStatus } from "src/gql/graphql";
import { CmSchema } from "src/gql/zod";
import { match } from "ts-pattern";
import { z } from "zod";

const Page = () => {
    const [search, setSearch] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);
    const { data } = useInfiniteQuery(query.users(search));
    const users = data?.pages.flat() ?? [];

    const aside = useAside();
    useHotkeys([["/", () => searchRef.current?.focus()]]);

    return (
        <Content paper>
            <Content.Heading backable>Manage Users</Content.Heading>

            <Content.Action>
                <Input
                    ref={searchRef}
                    variant="default"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftSection={<IconSearch size={16} />}
                    rightSection={<Kbd>/</Kbd>}
                    placeholder="Search by name..."
                />
            </Content.Action>

            <Table.ScrollContainer minWidth={"10vw"}>
                <Table striped tabularNums withRowBorders>
                    <Table.Thead>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Active</Table.Th>
                        <Table.Th>Access Tokens</Table.Th>
                        <Table.Th></Table.Th>
                    </Table.Thead>

                    <Table.Tbody>
                        {users.map((user) => (
                            <Table.Tr key={user.id}>
                                <Table.Td>
                                    {maybe(user.name)?.takeIf((it) => it.length > 0) ?? (
                                        <Text size="xs" c="dimmed">
                                            None
                                        </Text>
                                    )}
                                </Table.Td>
                                <Table.Td>{user.loginEmail}</Table.Td>
                                <Table.Td>
                                    {maybe(user.lastLogin)?.map(parseInt).take(getRelativeTime) ?? (
                                        <Text size="xs" c="dimmed">
                                            Never
                                        </Text>
                                    )}
                                </Table.Td>
                                <Table.Td>{user.accessTokens.length}</Table.Td>
                                <Table.Td>
                                    <ActionIcon
                                        variant="default"
                                        onClick={() =>
                                            aside.present({
                                                mode: "drawer",
                                                label: user.name ?? "Anonymous User",
                                                component: (
                                                    <Suspense fallback={"Loading..."}>
                                                        <UserDrawer userId={user.id} />
                                                    </Suspense>
                                                ),
                                            })
                                        }
                                        aria-label="Manage user"
                                    >
                                        <IconUserCog size={16} />
                                    </ActionIcon>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>

            <LoadMoreTrigger query={query.users(search)} />
        </Content>
    );
};

const UserDrawer = (props: { userId: string }) => {
    const { data } = useSuspenseQuery(query.user(props.userId));
    const { copy } = useClipboard();

    const expire = useGraphqlMutation(
        graphql(`
            mutation SupportExpireToken($id: UUID!) {
                expireAccessToken(id: $id) {
                    isExpired
                }
            }
        `),
    );

    const deleteEud = useGraphqlMutation(
        graphql(`
            mutation SupportDeleteEud($id: UUID!, $userId: UUID!) {
                removeEUD(eudId: $id, userId: $userId)
            }
        `),
    );

    function handleExpireToken(id: string) {
        notify
            .confirm("Are you sure you want to expire this access token? This operation cannot be undone.", {
                color: "red",
                label: "Expire Token",
            })
            .then(({ close }) => expire.mutateAsync({ id }).then(close))
            .then(() => qk.invalidate("support", "user"))
            .catch(notify.catch);
    }

    function handleDeleteEUD(id: string, userId: string) {
        notify
            .confirm("Are you sure you want to delete this EUD?", { color: "red", label: "Delete" })
            .then(({ close }) => deleteEud.mutateAsync({ id, userId }).then(close))
            .then(() => qk.invalidate("support", "user"))
            .catch(notify.catch);
    }

    function handleVerifyEUD(id: string, userId: string) {
        const Panel = () => {
            const code = useField({ initialValue: "" });

            const sendCode = useGraphqlMutation(
                graphql(`
                    mutation SupportVerifyEUDSendCode($id: UUID!, $userId: UUID!) {
                        resendEUDVerificationCode(eudId: $id, userId: $userId) {
                            id
                        }
                    }
                `),
            );

            const verifyCode = useGraphqlMutation(
                graphql(`
                    mutation SupportverifyEUD($id: UUID!, $userId: UUID!, $code: String!) {
                        verifyEUD(eudId: $id, userId: $userId, verificationCode: $code) {
                            isVerified
                        }
                    }
                `),
            );

            return (
                <Stack>
                    <TextInput label="Code" {...code.getInputProps()} />
                    <Divider mx="-md" />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => modals.closeAll()}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={() => sendCode.mutate({ id, userId })} loading={sendCode.isPending}>
                            Send Code
                        </Button>
                        <Button
                            onClick={() =>
                                void verifyCode
                                    .mutateAsync({ id, userId, code: code.getValue() })
                                    .then(({ verifyEUD }) => {
                                        if (verifyEUD.isVerified) return qk.invalidate("support", "user").then(() => modals.closeAll());
                                        notify.show.warn({ message: "Invalid Code" });
                                    })
                                    .catch(notify.catch)
                            }
                            loading={verifyCode.isPending}
                            disabled={verifyCode.isPending}
                        >
                            Submit
                        </Button>
                    </Group>
                </Stack>
            );
        };

        modals.open({
            title: <Title order={3}>Verify EUD</Title>,
            children: <Panel />,
        });
    }

    function handleCreateEUD(userId: string) {
        const Panel = () => {
            const createEud = useGraphqlMutation(
                graphql(`
                    mutation SupportCreateEUD($userId: UUID!, $name: String!, $type: CM!, $value: String!) {
                        addEUD(userId: $userId, eudParams: { name: $name, type: $type, value: $value, sendVerification: false }) {
                            id
                        }
                    }
                `),
            );

            const name = useField({
                initialValue: "New EUD",
                validate: (it) => (it.trim() == "" ? "Required" : null),
                validateOnChange: true,
            });
            const type = useField({ initialValue: Cm.Email });
            const value = useField({ initialValue: "", validate: (it) => (it.trim() == "" ? "Required" : null), validateOnChange: true });

            return (
                <Stack>
                    <Select data={Object.values(CmSchema.enum)} {...type.getInputProps()} />
                    <TextInput label="Name" {...name.getInputProps()} />
                    <TextInput label="Value" {...value.getInputProps()} />
                    <Divider mx="-md" />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => modals.closeAll()}>
                            Close
                        </Button>
                        <Button
                            onClick={() =>
                                void createEud
                                    .mutateAsync({ userId, name: name.getValue(), type: type.getValue(), value: value.getValue() })
                                    .then(() => qk.invalidate("support", "user").then(() => modals.closeAll()))
                                    .catch(notify.catch)
                            }
                        >
                            Create
                        </Button>
                    </Group>
                </Stack>
            );
        };

        modals.open({
            title: <Title order={3}>Create EUD</Title>,
            children: <Panel />,
        });
    }

    function handleCreateToken() {
        const Panel = () => {
            const createToken = useGraphqlMutation(
                graphql(`
                    mutation SupportCreateAccessToken($name: String!, $expiresAt: Instant, $userId: UUID!) {
                        createAccessToken(params: { name: $name, expiration: $expiresAt, userId: $userId }) {
                            token
                        }
                    }
                `),
            );

            const [expiration, setExpiration] = useState<Date | null>();
            const name = useField({ initialValue: "", validateOnChange: true, validate: (it) => (it.trim() == "" ? "Required" : null) });

            return (
                <Stack>
                    <TextInput label="Token Name" required {...name.getInputProps()} />
                    <DateTimePicker
                        label="Expires At"
                        value={expiration}
                        onChange={(next) => catching(() => setExpiration(z.coerce.date().nullable().parse(next)))}
                        clearable
                    />

                    <Divider mx="-md" />

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => modals.closeAll()}>
                            Cancel
                        </Button>
                        <Button
                            loading={createToken.isPending}
                            onClick={() => {
                                if (!!name.error) return;
                                createToken
                                    .mutateAsync({
                                        expiresAt: maybe(expiration)?.take((it) => (+it).toString()),
                                        name: name.getValue(),
                                        userId: props.userId,
                                    })
                                    .then(({ createAccessToken: { token } }) => copy(token))
                                    .then(() =>
                                        notify.show.success(
                                            "Access token copied to clipboard. Paste it somewhere safe— you won't be able to see it again.",
                                        ),
                                    )
                                    .then(() => qk.invalidate("support", "user"))
                                    .then(() => modals.closeAll())
                                    .catch(notify.catch);
                            }}
                        >
                            Create
                        </Button>
                    </Group>
                </Stack>
            );
        };

        modals.open({
            title: <Title order={3}>Create Access Token</Title>,
            children: <Panel />,
        });
    }

    return (
        <Stack>
            <Text size="sm" c="dimmed" mt="-md" style={{ zIndex: 1000 }}>
                {data.loginEmail}
            </Text>
            <Group gap="xs" w="fit-content">
                <Badge variant="light" color={data.language != Language.English ? "yellow" : "gray"}>
                    {titlecase(data.language)}
                </Badge>
                {match(data.status)
                    .with(UserStatus.Active, () => (
                        <Tooltip label={`Last active: ${data.statusSince}`}>
                            <Badge variant="light" color="green">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.Invited, () => (
                        <Tooltip label={`Invited on ${data.statusSince}`}>
                            <Badge variant="light" color="yellow">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.Inactive, () => (
                        <Tooltip label={`Inactive since: ${data.statusSince}`}>
                            <Badge variant="light" color="red">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .with(UserStatus.OptIn, () => (
                        <Tooltip label={`Joined on ${data.statusSince}`}>
                            <Badge variant="light" color="orange">
                                {data.status}
                            </Badge>
                        </Tooltip>
                    ))
                    .exhaustive()}

                <PropertyBadge title="Last Login">{maybe(data.lastLogin)?.map(parseInt).take(getRelativeTime) ?? "Never"}</PropertyBadge>
                <PropertyBadge title="SMS/Phone Euds">
                    {data.euds.filter((it) => it.type == Cm.Sms || it.type == Cm.Phone).length}
                </PropertyBadge>
                <PropertyBadge title="Native App EUDS">
                    {data.euds.filter((it) => it.type == Cm.Android || it.type == Cm.Ios).length}
                </PropertyBadge>
            </Group>

            <Divider mx="-md" />

            <Text fw="bold">Roles</Text>
            <Paper withBorder shadow="md" p="sm">
                <Table withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Role</Table.Th>
                            <Table.Th>Target</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.roles.length == 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={2} align="center">
                                    <Text size="sm" c="dimmed" fs="italic">
                                        No Roles
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {data.roles.map((each, i) => (
                            <Table.Tr key={i}>
                                <Table.Td>
                                    <Tooltip label={each.role.name}>
                                        <Badge variant="default">{each.role.name}</Badge>
                                    </Tooltip>
                                </Table.Td>
                                <Table.Td style={{ textDecoration: !!each.group || !!each.topic ? "underline" : "unset" }}>
                                    {maybe(each.group)?.take((it) => (
                                        <Link to="/organization/groups/$groupId" params={{ groupId: it.id }}>
                                            {it.name}
                                        </Link>
                                    ))}
                                    {maybe(each.topic)?.take((it) => (
                                        <Link to="/organization/topics/$topicId" params={{ topicId: it.id }}>
                                            {it.name}
                                        </Link>
                                    ))}
                                    {!each.group && !each.topic ? <Text size="sm">None</Text> : null}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>

            <Group justify="space-between">
                <Text fw="bold">Access Tokens</Text>
                <Button variant="default" onClick={handleCreateToken}>
                    Create
                </Button>
            </Group>
            <Paper withBorder shadow="md" p="sm">
                <Table withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>ID</Table.Th>
                            <Table.Th>Last Used</Table.Th>
                            <Table.Th>Expires</Table.Th>
                            <Table.Th></Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.accessTokens.length == 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={4} align="center">
                                    <Text size="sm" c="dimmed" fs="italic">
                                        No Access Tokens
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {data.accessTokens.map((each) => (
                            <Table.Tr key={each.id}>
                                <Table.Td>
                                    <Tooltip label={each.name}>
                                        <span>{each.id}</span>
                                    </Tooltip>
                                </Table.Td>
                                <Table.Td>
                                    <Tooltip
                                        label={
                                            maybe(each.lastUsedAt)
                                                ?.map(parseInt)
                                                .take((it) => new Date(it).toString()) ?? "Never"
                                        }
                                        hidden={!each.lastUsedAt}
                                    >
                                        <span>{maybe(each.lastUsedAt)?.map(parseInt).take(getRelativeTime) ?? "Never"}</span>
                                    </Tooltip>
                                </Table.Td>

                                <Table.Td>
                                    <Tooltip
                                        label={
                                            maybe(each.expiration)
                                                ?.map(parseInt)
                                                .take((it) => new Date(it).toString()) ?? "Never"
                                        }
                                        hidden={!each.expiration}
                                    >
                                        <Badge variant="light" color={each.isExpired ? "red" : "gray"}>
                                            {maybe(each.expiration)?.map(parseInt).take(getRelativeTime) ?? "Never"}
                                        </Badge>
                                    </Tooltip>
                                </Table.Td>
                                <Table.Td>
                                    <ActionIcon.Group>
                                        <ActionIcon
                                            variant="default"
                                            onClick={() => {
                                                copy(each.id);
                                                notify.show.success("Copied Token ID.");
                                            }}
                                            aria-label="Copy token"
                                        >
                                            <IconCopy size={16} />
                                        </ActionIcon>
                                        <ActionIcon variant="default" onClick={() => handleExpireToken(each.id)} aria-label="Expire token">
                                            <IconClockX size={16} />
                                        </ActionIcon>
                                    </ActionIcon.Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>

            <Group justify="space-between">
                <Text fw="bold">EUDs</Text>
                <Button variant="default" onClick={() => handleCreateEUD(data.id)}>
                    Create
                </Button>
            </Group>

            <Paper withBorder shadow="md" p="sm">
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Type</Table.Th>
                            <Table.Th>Verification</Table.Th>
                            <Table.Th>Value</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.euds.length == 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={4} align="center">
                                    <Text size="sm" c="dimmed" fs="italic">
                                        No EUDs
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                        {data.euds.map((eud) => (
                            <Table.Tr key={eud.id}>
                                <Table.Td>{eud.name}</Table.Td>
                                <Table.Td>{<Badge variant="default">{eud.type}</Badge>}</Table.Td>
                                <Table.Td>
                                    {
                                        <Badge variant="light" color={eud.isVerified ? "green" : "red"}>
                                            {eud.isVerified ? "Verified" : "Not Verified"}
                                        </Badge>
                                    }
                                </Table.Td>
                                <Table.Td>{eud.value}</Table.Td>
                                <Table.Td>
                                    <ActionIcon.Group>
                                        <ActionIcon
                                            variant="default"
                                            onClick={() => handleVerifyEUD(eud.id, data.id)}
                                            disabled={eud.isVerified}
                                            aria-label="Verify E.U.D."
                                        >
                                            <IconRosetteDiscountCheck size={16} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="default"
                                            onClick={() => handleDeleteEUD(eud.id, data.id)}
                                            aria-label="Delete E.U.D."
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </ActionIcon.Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
        </Stack>
    );
};

const query = {
    user: (id: string) =>
        queryOptions({
            queryKey: qk("support", "user", id),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query SupportManageUsersSpecificUser($id: UUID!) {
                            user(userId: $id) {
                                id
                                name
                                loginEmail
                                lastLogin
                                status
                                statusSince
                                euds {
                                    id
                                    type
                                    name
                                    isVerified
                                    value
                                }
                                roles {
                                    role {
                                        name
                                    }
                                    group {
                                        id
                                        name
                                    }
                                    topic {
                                        id
                                        name
                                    }
                                }
                                addresses {
                                    id
                                }
                                language
                                accessTokens {
                                    id
                                    name
                                    expiration
                                    lastUsedAt
                                    isExpired
                                }
                            }
                        }
                    `),
                    { id },
                ),
            select: ({ user }) => user,
        }),
    users: (query: string = "") =>
        createOffsetBasedGqlQueryOptions({
            queryKey: qk("support", "user-manage", query),
            document: graphql(`
                query SupportAccessTokenTable($offset: Int!, $limit: Int!, $query: String) {
                    users(limit: $limit, offset: $offset, nameQuery: $query) {
                        id
                        name
                        loginEmail
                        lastLogin
                        accessTokens {
                            id
                        }
                    }
                }
            `),
            variables: { query: some(query).takeIf((it) => it.trim().length > 0) },
            select: ({ users }) => users,
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/manage-users")({
    component: Page,
});
